/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, Plus, MessagesSquare, Lightbulb } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError, useAuth } from "@/lib/auth";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import { MESSAGE_CATEGORIES, labelFor, relativeTime } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function PortalMessages() {
    const { activeClientId, activeClient } = useAuth();
    const [threads, setThreads] = useState([]);
    const [selected, setSelected] = useState(null);
    const [thread, setThread] = useState(null);
    const [reply, setReply] = useState("");
    const [suggested, setSuggested] = useState([]);
    const [articleCache, setArticleCache] = useState({}); // id -> article
    const [busy, setBusy] = useState(false);

    async function loadThreads() {
        try {
            const params = activeClientId ? { client_id: activeClientId } : {};
            const { data } = await api.get("/messages/threads", { params });
            setThreads(data);
            if (data.length && !selected) setSelected(data[0].id);
            if (data.length === 0) { setSelected(null); setThread(null); }
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }
    useEffect(() => { setSelected(null); setThread(null); loadThreads(); /* eslint-disable-line */ }, [activeClientId]);
    useEffect(() => {
        if (!selected) return;
        api.get(`/messages/threads/${selected}`).then((r) => setThread(r.data)).catch(() => {});
    }, [selected]);

    // Hydrate any suggested help article IDs into cached titles
    useEffect(() => {
        const ids = new Set();
        thread?.messages?.forEach((m) => (m.suggested_articles || []).forEach((id) => ids.add(id)));
        const missing = [...ids].filter((id) => !articleCache[id]);
        missing.forEach(async (id) => {
            try {
                const { data } = await api.get(`/help/${id}`);
                setArticleCache((c) => ({ ...c, [id]: data }));
            } catch (e) { /* ignore */ }
        });
    }, [thread, articleCache]);

    // Live keyword-based suggestions as user types reply
    useEffect(() => {
        const t = setTimeout(async () => {
            if (!reply.trim() || reply.length < 5) {
                setSuggested([]);
                return;
            }
            try {
                const { data } = await api.post(`/messages/suggest`, null, { params: { body: reply } });
                setSuggested(data || []);
            } catch (e) {
                setSuggested([]);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [reply]);

    async function sendReply() {
        if (!reply.trim()) return;
        setBusy(true);
        try {
            await api.post(`/messages/threads/${selected}/reply`, { body: reply });
            setReply("");
            const { data } = await api.get(`/messages/threads/${selected}`);
            setThread(data);
            await loadThreads();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div>
            <div className="flex items-end justify-between">
                <div>
                    <div className="co-label">Messages</div>
                    <h1 className="font-display text-3xl tracking-tight font-medium mt-1">Talk to your accountant</h1>
                    <p className="text-sm text-[#5E636E] mt-2">One place for every conversation — categorised by type.</p>
                </div>
                <NewThreadDialog onCreated={loadThreads} activeClientId={activeClientId} activeClientName={activeClient?.name} />
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-[280px_1fr]">
                {/* Thread list */}
                <div className="co-card overflow-hidden">
                    <div className="border-b border-[#E4E4E7] px-4 py-3 co-label">Conversations</div>
                    <ul className="divide-y divide-[#E4E4E7] max-h-[520px] overflow-y-auto" data-testid="portal-threads">
                        {threads.length === 0 && <li className="p-4 text-sm text-[#5E636E]">No conversations yet.</li>}
                        {threads.map((t) => (
                            <li key={t.id}>
                                <button
                                    onClick={() => setSelected(t.id)}
                                    className={`w-full text-left p-3 hover:bg-zinc-50 ${selected === t.id ? "bg-zinc-50" : ""}`}
                                    data-testid={`portal-thread-${t.id}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-sm font-medium truncate">{t.subject}</span>
                                        <StatusBadge status={t.status} />
                                    </div>
                                    <div className="text-[11px] text-[#5E636E] mt-0.5">{labelFor(MESSAGE_CATEGORIES, t.category)} · {relativeTime(t.last_message_at)}</div>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Thread detail */}
                <div className="co-card flex flex-col overflow-hidden min-h-[520px]">
                    {!thread && <EmptyState icon={MessagesSquare} title="Pick a conversation" description="Or start a new one with the button above." />}
                    {thread && (
                        <>
                            <div className="border-b border-[#E4E4E7] px-5 py-4">
                                <div className="font-display text-lg font-medium" data-testid="portal-thread-subject">{thread.thread.subject}</div>
                                <div className="mt-1 flex items-center gap-2 text-[11px] text-[#5E636E]">
                                    <StatusBadge status={thread.thread.status} />
                                    <span>·</span>
                                    <span>{labelFor(MESSAGE_CATEGORIES, thread.thread.category)}</span>
                                </div>
                            </div>
                            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="portal-messages-stream">
                                {thread.messages.map((m) => (
                                    <div key={m.id}>
                                        <div className={`flex ${m.sender_role === "client" ? "justify-end" : "justify-start"}`}>
                                            <div className={`max-w-[80%] rounded-lg border px-3 py-2 ${m.sender_role === "client" ? "bg-[#0F1115] text-white border-[#0F1115]" : "bg-[#FAFAFA] border-[#E4E4E7]"}`}>
                                                <div className="text-[10px] uppercase tracking-wider opacity-70 mb-1">
                                                    {m.sender_role === "client" ? "You" : m.sender_role.replace("_", " ")} · {relativeTime(m.created_at)}
                                                </div>
                                                <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                                            </div>
                                        </div>
                                        {/* Suggested help articles from a client message */}
                                        {m.sender_role === "client" && (m.suggested_articles || []).length > 0 && (
                                            <div className="mt-2 ml-auto max-w-[80%] flex justify-end">
                                                <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 w-full">
                                                    <div className="font-medium flex items-center gap-1"><Lightbulb className="h-3.5 w-3.5" /> You may also find these helpful</div>
                                                    <ul className="mt-1 space-y-0.5">
                                                        {m.suggested_articles.map((aid) => (
                                                            <li key={aid}>· {articleCache[aid]?.title || "Loading…"}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div className="border-t border-[#E4E4E7] px-5 py-4 space-y-2">
                                {suggested.length > 0 && (
                                    <div className="rounded-md border border-blue-200 bg-blue-50 p-2 text-xs text-blue-900" data-testid="portal-suggestions">
                                        <div className="font-medium flex items-center gap-1"><Lightbulb className="h-3.5 w-3.5" /> Quick help</div>
                                        <ul className="mt-1 space-y-0.5">
                                            {suggested.map((s) => <li key={s.id}>· {s.title}</li>)}
                                        </ul>
                                    </div>
                                )}
                                <Textarea
                                    rows={3}
                                    placeholder="Type your question…"
                                    value={reply}
                                    onChange={(e) => setReply(e.target.value)}
                                    data-testid="portal-reply-textarea"
                                />
                                <div className="flex justify-end">
                                    <Button onClick={sendReply} disabled={busy || !reply.trim()} className="bg-[#171717] hover:bg-[#2E2E2E] text-white" data-testid="portal-reply-send">
                                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send"}
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function NewThreadDialog({ onCreated, activeClientId, activeClientName }) {
    const [open, setOpen] = useState(false);
    const [subject, setSubject] = useState("");
    const [category, setCategory] = useState("general");
    const [initial_body, setBody] = useState("");
    const [suggested, setSuggested] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const t = setTimeout(async () => {
            if (initial_body.length < 6) { setSuggested([]); return; }
            try {
                const { data } = await api.post("/messages/suggest", null, { params: { body: initial_body } });
                setSuggested(data || []);
            } catch (e) { setSuggested([]); }
        }, 300);
        return () => clearTimeout(t);
    }, [initial_body]);

    async function submit() {
        if (!subject || !initial_body) return;
        if (!activeClientId) {
            toast.error("Pick an entity from the top-right switcher first");
            return;
        }
        setBusy(true);
        try {
            await api.post("/messages/threads", { subject, category, initial_body, client_id: activeClientId });
            toast.success(`Conversation started${activeClientName ? ` for ${activeClientName}` : ""}`);
            setOpen(false);
            setSubject(""); setBody(""); setCategory("general");
            onCreated?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="bg-[#171717] hover:bg-[#2E2E2E] text-white" data-testid="portal-new-thread-btn"><Plus className="h-4 w-4" /> Ask</Button>
            </DialogTrigger>
            <DialogContent data-testid="portal-new-thread-dialog">
                <DialogHeader><DialogTitle>Ask your accountant</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div><Label>Subject</Label><Input className="mt-1" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="pnt-subject" /></div>
                    <div>
                        <Label>Type of question</Label>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger className="mt-1" data-testid="pnt-category"><SelectValue /></SelectTrigger>
                            <SelectContent>{MESSAGE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div><Label>Question</Label><Textarea rows={5} className="mt-1" value={initial_body} onChange={(e) => setBody(e.target.value)} data-testid="pnt-body" /></div>
                    {suggested.length > 0 && (
                        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900" data-testid="pnt-suggestions">
                            <div className="font-medium flex items-center gap-1"><Lightbulb className="h-3.5 w-3.5" /> Before you send, these may already help</div>
                            <ul className="mt-1 space-y-0.5">
                                {suggested.map((s) => <li key={s.id}>· {s.title}</li>)}
                            </ul>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)} data-testid="pnt-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="bg-[#171717] hover:bg-[#2E2E2E] text-white" data-testid="pnt-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send question"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
